import type { Location } from "@sitescene/types";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { locationsApi } from "@/api/locations";
import { queryKeys } from "@/api/query-keys";
import { noRetryOn4xx, useQueryErrorToast } from "@/hooks/useQueryErrorToast";
import { useAuth } from "@/providers/auth-provider";

/**
 * Page data structure that includes both locations and pagination metadata
 */
interface LocationsPage {
	data: Location[];
	endCursor?: string;
	hasMore?: boolean;
}

/**
 * Hook for fetching all locations
 *
 * Uses centralized query keys and pure API functions.
 * Only fetches when user is authenticated to prevent 401 errors.
 */
export function useGetLocations(options?: {
	includeZonesAndSensors?: boolean;
}) {
	const { isAuthenticated, isLoadingAuth } = useAuth();
	const includeZonesAndSensors = options?.includeZonesAndSensors;

	const queryKey = includeZonesAndSensors
		? queryKeys.locations.list({ includeZonesAndSensors })
		: queryKeys.locations.lists();

	const {
		data: locationsData,
		error: locationsError,
		isError: isLocationsError,
		isLoading: isLoadingLocations,
		isSuccess: isLocationsSuccess,
		refetch: refetchLocations,
	} = useQuery<Location[]>({
		enabled: isAuthenticated && !isLoadingAuth,
		queryFn: () => locationsApi.getLocations({ includeZonesAndSensors }),
		queryKey,
		retry: noRetryOn4xx,
		staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
	});

	useQueryErrorToast({
		error: locationsError,
		isError: isLocationsError,
		title: "Failed to load locations",
	});

	return {
		isLoadingLocations,
		isLocationsError,
		isLocationsSuccess,
		locationsData,
		locationsError,
		refetchLocations,
	};
}

/**
 * Hook for fetching paginated locations with cursor-based pagination
 *
 * Uses infinite query pattern for efficient loading of paginated data.
 * Automatically handles cursor-based pagination from the API.
 * Only fetches when user is authenticated to prevent 401 errors.
 */
export function useLocationsPaginated(limit: number = 100) {
	const { isAuthenticated, isLoadingAuth } = useAuth();

	const {
		data: locationsPaginatedData,
		error: locationsPaginatedError,
		fetchNextPage: fetchNextPageLocations,
		hasNextPage: hasNextPageLocations,
		isFetchingNextPage: isFetchingNextPageLocations,
		isLoading: isLoadingLocationsPaginated,
		refetch: refetchLocationsPaginated,
	} = useInfiniteQuery<LocationsPage, Error>({
		enabled: isAuthenticated && !isLoadingAuth,
		getNextPageParam: (lastPage: LocationsPage) => {
			// Return the endCursor if there are more pages, otherwise undefined
			return lastPage.hasMore && lastPage.endCursor
				? lastPage.endCursor
				: undefined;
		},
		initialPageParam: undefined,
		queryFn: async ({ pageParam }) => {
			const response = await locationsApi.getLocationsPaginated(
				limit,
				pageParam as string | undefined,
			);
			// Return both data and pagination info
			return {
				data: response.data,
				endCursor: response.endCursor,
				hasMore: response.hasMore ?? false,
			};
		},
		queryKey: queryKeys.locations.listPaginated(),
		staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
	});

	return {
		fetchNextPageLocations,
		hasNextPageLocations,
		isFetchingNextPageLocations,
		isLoadingLocationsPaginated,
		locationsPaginatedData,
		locationsPaginatedError,
		refetchLocationsPaginated,
	};
}
